import { CheckCircle2, Globe2, Wrench } from "lucide-react";
import { COMPANY, IMAGES } from "../../lib/constants";

const PILLARS = [
  {
    icon: CheckCircle2,
    title: "Traceability First",
    body: "Every part shipped carries the documentation auditors expect — FAA 8130-3, EASA Form 1, and back-to-birth records when available.",
  },
  {
    icon: Globe2,
    title: "Global Distribution",
    body: "A vetted forwarder network and consolidated logistics partners keep parts moving across borders, time zones, and AOG clocks.",
  },
  {
    icon: Wrench,
    title: "Operator Mindset",
    body: "We think like the people we serve — quote fast, source faster, and answer the phone day or night. No voicemails, no delays, no runaround. Just solutions.",
  },
];

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative bg-white py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <p className="nps-eyebrow nps-divider-gold mb-5">
              About {COMPANY.short}
            </p>
            <h2 className="font-display font-bold text-[#0B1D3A] text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight">
              About Noble Power
              <br />
              <span className="text-[#C9A961]">Solutions</span>
            </h2>

            <div className="mt-7 space-y-5 text-[#4B5563] leading-relaxed text-[15px]">
              <p>
                {COMPANY.name} is an aviation aftermarket company specializing
                in Engine Sales, Engine Piece Parts Sales, and Inventory
                Procurement. We serve operators and maintenance providers who
                depend on fast, accurate, and fully documented solutions across
                some of the most widely operated commercial platforms in the
                world.
              </p>
              <p>
                We built this company around one standard: Integrity! Every
                part we source is verified. Every document is clean, complete,
                and ready for the most demanding inspection. When an aircraft
                is on the ground and time is critical, we respond with urgency
                and execute with precision.
              </p>
              <p>
                Our global network of trusted suppliers and certified MRO
                partners gives us the reach to deliver solutions that larger,
                slower organizations simply cannot match — backed by the
                personal accountability that only a dedicated specialist can
                provide.
              </p>
            </div>

            <p className="mt-8 pt-6 border-t border-[#E5E7EB] font-display font-bold text-[#0B1D3A] text-xl italic">
              <span>Built on Trust.</span>{" "}
              <span className="text-[#C9A961]">Driven by Results.</span>
            </p>
          </div>

          <div className="lg:col-span-7">
            <div className="relative">
              <div className="relative overflow-hidden rounded-sm">
                <img
                  src={IMAGES.about}
                  alt="Commercial aircraft in flight"
                  className="w-full h-[400px] lg:h-[520px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#0B1D3A]/30 via-transparent to-transparent" />
              </div>
              <div className="absolute -bottom-6 -left-6 hidden lg:block bg-[#0B1D3A] text-white p-6 max-w-[260px] border-l-[3px] border-[#C9A961]">
                <p className="font-display font-bold text-lg leading-snug">
                  &ldquo;Sourced with Purpose. Delivered with Integrity.&rdquo;
                </p>
                <p className="text-xs uppercase tracking-[0.18em] text-[#C9A961] mt-3">
                  Our Operating Principle
                </p>
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-5 mt-12">
              {PILLARS.map((p) => {
                const Icon = p.icon;
                return (
                  <div
                    key={p.title}
                    data-testid={`about-pillar-${p.title.replace(/\s+/g, "-").toLowerCase()}`}
                    className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-sm p-5 hover:border-[#1E3A5F] transition-colors"
                  >
                    <Icon className="w-6 h-6 text-[#C9A961]" />
                    <h3 className="font-display font-semibold text-[#0B1D3A] mt-4 text-base">
                      {p.title}
                    </h3>
                    <p className="text-[13px] text-[#4B5563] mt-2 leading-relaxed">
                      {p.body}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
